<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type IustaSettings,
    type IustaSettingsRequest,
    type CustomerUser,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Iusta integration fields
  const isIustaEnabled = ref(false)
  const iustaApiKey = ref('')
  const originalIustaApiKey = ref('')
  const iustaApiBaseUrl = ref('')
  const iustaCaseGroupId = ref<number | null>(null)
  const iustaJsonCategoryId = ref<number | null>(null)
  const iustaTextCategoryId = ref<number | null>(null)
  const iustaDocumentCategoryId = ref<number | null>(null)
  const iustaWorkflowInstanceId = ref<number | null>(null)
  const iustaJupusTagId = ref<number | null>(null)
  const iustaDateTagId = ref<number | null>(null)
  const iustaLawFieldTagId = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description:
        'Configure Iusta integration settings. This allows JUPUS to export case data to your Iusta system.',
      enableIusta: 'Enable Iusta Integration',
      // Credentials section
      credentialsSection: 'Credentials',
      apiKey: 'API Key',
      apiKeyPlaceholder: 'Enter Iusta API key',
      apiBaseUrl: 'API Base URL',
      apiBaseUrlPlaceholder: 'https://api.ihrname.iusta.io/api',
      // Category IDs section
      categoryIdsSection: 'Category IDs',
      caseGroupId: 'Case Group ID',
      caseGroupIdPlaceholder: 'Enter case group ID',
      jsonCategoryId: 'JSON Category ID',
      jsonCategoryIdPlaceholder: 'Enter JSON category ID',
      textCategoryId: 'Text Category ID',
      textCategoryIdPlaceholder: 'Enter text category ID',
      documentCategoryId: 'Document Category ID',
      documentCategoryIdPlaceholder: 'Enter document category ID',
      workflowInstanceId: 'Workflow Instance ID',
      workflowInstanceIdPlaceholder: 'Enter workflow instance ID',
      // Tag IDs section
      tagIdsSection: 'Tag IDs',
      jupusTagId: 'JUPUS Tag ID',
      jupusTagIdPlaceholder: 'Enter JUPUS tag ID',
      dateTagId: 'Date Tag ID',
      dateTagIdPlaceholder: 'Enter date tag ID',
      lawFieldTagId: 'Law Field Tag ID',
      lawFieldTagIdPlaceholder: 'Enter law field tag ID'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description:
        'Konfigurieren Sie die Iusta-Integrationseinstellungen. Damit kann JUPUS Falldaten in Ihr Iusta-System exportieren.',
      enableIusta: 'Iusta Integration aktivieren',
      // Credentials section
      credentialsSection: 'Zugangsdaten',
      apiKey: 'API Key',
      apiKeyPlaceholder: 'Iusta API Key eingeben',
      apiBaseUrl: 'API Basis-URL',
      apiBaseUrlPlaceholder: 'https://api.ihrname.iusta.io/api',
      // Category IDs section
      categoryIdsSection: 'Kategorie-IDs',
      caseGroupId: 'Fallgruppen-ID',
      caseGroupIdPlaceholder: 'Fallgruppen-ID eingeben',
      jsonCategoryId: 'JSON-Kategorie-ID',
      jsonCategoryIdPlaceholder: 'JSON-Kategorie-ID eingeben',
      textCategoryId: 'Text-Kategorie-ID',
      textCategoryIdPlaceholder: 'Text-Kategorie-ID eingeben',
      documentCategoryId: 'Dokument-Kategorie-ID',
      documentCategoryIdPlaceholder: 'Dokument-Kategorie-ID eingeben',
      workflowInstanceId: 'Workflow-Instanz-ID',
      workflowInstanceIdPlaceholder: 'Workflow-Instanz-ID eingeben',
      // Tag IDs section
      tagIdsSection: 'Tag-IDs',
      jupusTagId: 'JUPUS Tag-ID',
      jupusTagIdPlaceholder: 'JUPUS Tag-ID eingeben',
      dateTagId: 'Datum Tag-ID',
      dateTagIdPlaceholder: 'Datum Tag-ID eingeben',
      lawFieldTagId: 'Rechtsgebiet Tag-ID',
      lawFieldTagIdPlaceholder: 'Rechtsgebiet Tag-ID eingeben'
    }
  })

  const updateIustaSettings = async () => {
    try {
      isLoading.value = true

      const data: IustaSettingsRequest = {
        is_iusta_egress_integration_enabled: isIustaEnabled.value
      }

      // Only include API key if it has changed from the original
      if (iustaApiKey.value !== originalIustaApiKey.value) {
        data.iusta_api_key = iustaApiKey.value
      }

      if (iustaApiBaseUrl.value) {
        data.iusta_api_base_url = iustaApiBaseUrl.value
      }

      if (iustaCaseGroupId.value !== null) {
        data.iusta_case_group_id = iustaCaseGroupId.value
      }

      if (iustaJsonCategoryId.value !== null) {
        data.iusta_json_category_id = iustaJsonCategoryId.value
      }

      if (iustaTextCategoryId.value !== null) {
        data.iusta_text_category_id = iustaTextCategoryId.value
      }

      if (iustaDocumentCategoryId.value !== null) {
        data.iusta_document_category_id = iustaDocumentCategoryId.value
      }

      if (iustaWorkflowInstanceId.value !== null) {
        data.iusta_workflow_instance_id = iustaWorkflowInstanceId.value
      }

      if (iustaJupusTagId.value !== null) {
        data.iusta_jupus_tag_id = iustaJupusTagId.value
      }

      if (iustaDateTagId.value !== null) {
        data.iusta_date_tag_id = iustaDateTagId.value
      }

      if (iustaLawFieldTagId.value) {
        data.iusta_law_field_tag_id = iustaLawFieldTagId.value
      }

      await SettingsService.settingsIustaCreate({
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const settings = (await SettingsService.settingsIustaList()) as IustaSettings

      if (settings) {
        isIustaEnabled.value = settings.is_iusta_egress_integration_enabled || false
        iustaApiKey.value = settings.iusta_api_key || ''
        originalIustaApiKey.value = settings.iusta_api_key || ''
        iustaApiBaseUrl.value = settings.iusta_api_base_url || ''
        iustaCaseGroupId.value = settings.iusta_case_group_id ?? null
        iustaJsonCategoryId.value = settings.iusta_json_category_id ?? null
        iustaTextCategoryId.value = settings.iusta_text_category_id ?? null
        iustaDocumentCategoryId.value = settings.iusta_document_category_id ?? null
        iustaWorkflowInstanceId.value = settings.iusta_workflow_instance_id ?? null
        iustaJupusTagId.value = settings.iusta_jupus_tag_id ?? null
        iustaDateTagId.value = settings.iusta_date_tag_id ?? null
        iustaLawFieldTagId.value = settings.iusta_law_field_tag_id || ''
      }
    } catch (error) {
      console.error('Error loading Iusta settings:', error)
      Sentry.captureException(error)
    }
  })

  // Watch for enable toggle
  watch([isIustaEnabled], () => {
    if (user.value) {
      updateIustaSettings()
    }
  })

  // Watch for credential fields
  watch([iustaApiKey, iustaApiBaseUrl], () => {
    if (user.value) {
      updateIustaSettings()
    }
  })

  // Watch for category ID fields
  watch(
    [
      iustaCaseGroupId,
      iustaJsonCategoryId,
      iustaTextCategoryId,
      iustaDocumentCategoryId,
      iustaWorkflowInstanceId
    ],
    () => {
      if (user.value) {
        updateIustaSettings()
      }
    }
  )

  // Watch for tag ID fields
  watch([iustaJupusTagId, iustaDateTagId, iustaLawFieldTagId], () => {
    if (user.value) {
      updateIustaSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <p class="mb-4 text-sm text-gray-600">{{ t('description') }}</p>

        <div class="max-w-xl space-y-6">
          <CheckboxField
            v-model="isIustaEnabled"
            name="is_iusta_egress_integration_enabled"
            :label="t('enableIusta')"
          />

          <div v-if="isIustaEnabled">
            <!-- Credentials Section -->
            <div class="mb-6">
              <h3 class="mb-3 text-sm font-semibold text-gray-900">
                {{ t('credentialsSection') }}
              </h3>
              <div class="space-y-4">
                <InputField
                  v-model="iustaApiKey"
                  name="iusta_api_key"
                  :label="t('apiKey')"
                  :placeholder="t('apiKeyPlaceholder')"
                  type="password"
                />

                <InputField
                  v-model="iustaApiBaseUrl"
                  name="iusta_api_base_url"
                  :label="t('apiBaseUrl')"
                  :placeholder="t('apiBaseUrlPlaceholder')"
                  type="url"
                />
              </div>
            </div>

            <!-- Category IDs Section -->
            <div class="mb-6">
              <h3 class="mb-3 text-sm font-semibold text-gray-900">
                {{ t('categoryIdsSection') }}
              </h3>
              <div class="space-y-4">
                <InputField
                  v-model.number="iustaCaseGroupId"
                  name="iusta_case_group_id"
                  :label="t('caseGroupId')"
                  :placeholder="t('caseGroupIdPlaceholder')"
                  type="number"
                />

                <InputField
                  v-model.number="iustaJsonCategoryId"
                  name="iusta_json_category_id"
                  :label="t('jsonCategoryId')"
                  :placeholder="t('jsonCategoryIdPlaceholder')"
                  type="number"
                />

                <InputField
                  v-model.number="iustaTextCategoryId"
                  name="iusta_text_category_id"
                  :label="t('textCategoryId')"
                  :placeholder="t('textCategoryIdPlaceholder')"
                  type="number"
                />

                <InputField
                  v-model.number="iustaDocumentCategoryId"
                  name="iusta_document_category_id"
                  :label="t('documentCategoryId')"
                  :placeholder="t('documentCategoryIdPlaceholder')"
                  type="number"
                />

                <InputField
                  v-model.number="iustaWorkflowInstanceId"
                  name="iusta_workflow_instance_id"
                  :label="t('workflowInstanceId')"
                  :placeholder="t('workflowInstanceIdPlaceholder')"
                  type="number"
                />
              </div>
            </div>

            <!-- Tag IDs Section -->
            <div class="mb-6">
              <h3 class="mb-3 text-sm font-semibold text-gray-900">{{ t('tagIdsSection') }}</h3>
              <div class="space-y-4">
                <InputField
                  v-model.number="iustaJupusTagId"
                  name="iusta_jupus_tag_id"
                  :label="t('jupusTagId')"
                  :placeholder="t('jupusTagIdPlaceholder')"
                  type="number"
                />

                <InputField
                  v-model.number="iustaDateTagId"
                  name="iusta_date_tag_id"
                  :label="t('dateTagId')"
                  :placeholder="t('dateTagIdPlaceholder')"
                  type="number"
                />

                <InputField
                  v-model="iustaLawFieldTagId"
                  name="iusta_law_field_tag_id"
                  :label="t('lawFieldTagId')"
                  :placeholder="t('lawFieldTagIdPlaceholder')"
                  type="text"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
